import { EventCard } from '~/components/EventCard'
import type { CalendarEntry } from '~/api/types'

import { weeksOf } from './MonthGrid'
import type { Me } from './MonthGrid'
import { onList } from './views'

import s from './PhoneMonth.module.css'

/**
 * The month, on a phone: a dot per gig, and the day you tap underneath it.
 *
 * This existed in the Python page as `paintDots` and the port never carried it over —
 * `MonthGrid`'s own docstring has been promising it all along ("the phone renders the same
 * month as a grid of dots, which is a different component; this one is only ever drawn
 * above 720px") and both halves of that sentence were false: there was no such component,
 * and nothing gated `MonthGrid` by width. So a phone got the desktop month.
 *
 * Measured at 390px before this was written: 51px cells, 42 of them, **40 photograph
 * thumbnails**, each chip a picture and a single truncated letter — "A", "J", "V". The
 * Python's note had done the arithmetic years earlier and reached the same place: "at 320px
 * a column is 47px: a chip there reads 'Ariana…' on all eight nights of a residency, which
 * is the same six characters and no more information than the dot."
 *
 * So the cell answers *how much, and have I decided*; the day you tap answers *what*. That
 * division is the whole design and it is why a dot beats a name here rather than merely
 * fitting better.
 *
 * The agenda underneath is `EventCard`, the same card the front page draws — not a fourth
 * copy of a gig row. A phone shows one column of them, which is what the front page does at
 * this width anyway. The faces on it are asked for larger than the card's own: the poster
 * here is the width of the screen and the reader's thumb is the pointer, so a face has to
 * be recognised without a hover and pressed without a miss.
 */

/** Four, then a count: five dots at 4px plus their gaps is wider than a 51px cell, and a row
 *  that wraps takes the day number with it. The Python found this the same way. */
const MOST_DOTS = 4

const DOW = ['M', 'T', 'W', 'T', 'F', 'S', 'S']

function iso(day: Date): string {
  return day.toISOString().slice(0, 10)
}

/** What a dot says: going, or on your list, or merely on. */
function dotOf(entry: CalendarEntry, me: Me | null): string {
  if (entry.going) return s.go ?? ''
  return onList(entry, me) ? (s.pick ?? '') : ''
}

export interface PhoneMonthProps {
  entries: CalendarEntry[]
  /**
   * What each night would send with the cap lifted — `{iso: total}`. Not what was on the
   * night: one that has gone counts only the rows somebody claimed, because the payload
   * sends nothing else from it.
   *
   * The badge beside the dots is this grid's only way of saying a night has more than it is
   * showing, and it has `MonthGrid`'s problem for `MonthGrid`'s reason: the payload sends a
   * busy night a fixed few unclaimed gigs, about as many as there are dots, so a count taken
   * off the rows is silent on the nights with most behind them. Behind the reader there is
   * nothing more to show, so the badge is meant to stay away — count the whole night instead
   * and it appears beside a day with no dots.
   *
   * Optional, and left out wherever this grid is not drawing the population the numbers
   * count — see `MonthGridProps.nights`, which this is the same key from the same payload.
   */
  nights?: Record<string, number> | undefined
  /** Today, as the server saw it. Never derived from the browser clock — see `MonthGrid`. */
  today: string
  /** `YYYY-MM`. */
  month: string
  me: Me | null
  /** The day whose gigs are listed underneath, and how to change it. */
  selected: string | null
  onSelect(night: string): void
  /**
   * Whether a night's rows are still on their way — see `MonthGridProps.pending`, which this
   * is the same answer from the same calendar. Left out, every night is taken as arrived.
   */
  pending?: ((night: string) => boolean) | undefined
}

export function PhoneMonth({
  entries,
  nights,
  today,
  month,
  me,
  selected,
  onSelect,
  pending,
}: PhoneMonthProps) {
  const weeks = weeksOf(month)
  const byDay = new Map<string, CalendarEntry[]>()
  for (const entry of entries) {
    // `iso`, the field the month grid groups on — the night a gig belongs to on the 05:00
    // rule, decided by the server. Not `startsAt`, which puts a 00:30 set on the next day.
    const run = byDay.get(entry.iso)
    if (run) run.push(entry)
    else byDay.set(entry.iso, [entry])
  }

  // Whatever is open stays open if it is still this month; otherwise today, and failing
  // that the first day of the month with anything on it. A month you page into should not
  // land on an empty agenda when it has gigs in it.
  const mine = weeks.flat().filter(d => iso(d).slice(0, 7) === month).map(iso)
  const open =
    selected && mine.includes(selected)
      ? selected
      : mine.includes(today)
        ? today
        : (mine.find(night => (byDay.get(night) ?? []).length) ?? mine[0] ?? today)

  const list = byDay.get(open) ?? []
  const openDue = !!pending?.(open)

  return (
    <>
      <div className={s.grid}>
        {DOW.map((letter, i) => (
          <div className={s.dow} key={i}>
            {letter}
          </div>
        ))}
        {weeks.flat().map(day => {
          const night = iso(day)
          const on = byDay.get(night) ?? []
          /*
           * The night, not the rows that arrived, and the same expression `MonthGrid` uses
           * for the same reason: the cap makes the rows fewer than the night, so a badge
           * counted off them disappears on exactly the nights it is for. The larger of the
           * two, so the badge can never say less than the dots it is standing beside — the
           * prop is optional and a night can be missing from it.
           */
          const onNight = Math.max(nights?.[night] ?? 0, on.length)
          const drawn = Math.min(on.length, MOST_DOTS)
          const away = night.slice(0, 7) !== month
          const due = !!pending?.(night)
          return (
            <button
              type="button"
              key={night}
              aria-busy={due || undefined}
              className={[
                s.cell,
                away ? s.out : '',
                night === today ? s.today : '',
                // A night behind us. Without it the first ten cells of a month read exactly
                // like the last twenty, and the grid's whole job is to show at a glance what
                // is left — a reader had to find today and work rightwards to know.
                night < today ? s.gone : '',
                night === open ? s.sel : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-pressed={night === open}
              /* The night's own count, so the label and the badge beside it are one number
                 read two ways rather than two numbers that disagree. A night still waiting
                 has no count yet, and a count taken off what has arrived would be a guess. */
              aria-label={
                due
                  ? `${day.getUTCDate()}`
                  : `${day.getUTCDate()} — ${onNight} ${onNight === 1 ? 'gig' : 'gigs'}`
              }
              onClick={() => onSelect(night)}
            >
              <span className={s.d}>{day.getUTCDate()}</span>
              <span className={s.dots}>
                {on.slice(0, MOST_DOTS).map(entry => (
                  <i className={dotOf(entry, me)} key={entry.id} />
                ))}
                {onNight > drawn ? <b>+{onNight - drawn}</b> : null}
                {/* Not an `<i>`: the dots are, and this is not a gig. */}
                {due ? <span className={s.pending} aria-hidden="true" /> : null}
              </span>
            </button>
          )
        })}
      </div>

      <div className={s.agenda}>
        {list.length ? (
          list.map(entry => <EventCard event={entry} key={entry.id} />)
        ) : openDue ? null : (
          <p className={s.empty}>Nothing on this day.</p>
        )}
      </div>
    </>
  )
}
